import React, { useEffect, useRef, useState } from "react";
import { SMART_VIEWS } from "../constants";
import { classNames } from "./utils";

function countForSmartView(key, { overview, queueRows, activityRows, memberRows }) {
  if (key === "queue") return queueRows?.length || 0;
  if (key === "replies") {
    const inbound = (activityRows || []).filter(
      (row) => row.activity?.direction === "inbound"
    );
    return inbound.length;
  }
  if (key === "followups") {
    return overview?.metrics?.dueSoon || overview?.metrics?.pendingQueue || 0;
  }
  if (key === "today") {
    return overview?.metrics?.dueToday || 0;
  }
  if (key === "all") {
    return overview?.metrics?.enrolled || memberRows?.length || 0;
  }
  return 0;
}

function ToneDot({ tone }) {
  const toneClass = {
    danger: "bg-red-500",
    success: "bg-emerald-500",
    primary: "bg-indigo-500",
    info: "bg-sky-500",
    warning: "bg-amber-500",
    default: "bg-zinc-400",
  }[tone] || "bg-zinc-400";
  return <span className={`h-1.5 w-1.5 rounded-full ${toneClass}`} />;
}

export default function CampaignRail({
  campaigns,
  activeCampaignId,
  loadingCampaigns,
  smartView,
  onSmartViewChange,
  onCampaignChange,
  onImport,
  onTemplates,
  onNewCampaign,
  onOpenSettings,
  onSegmentSelect,
  onSegmentDelete,
  overview,
  queueRows,
  activityRows,
  memberRows,
  gmailConnected,
  onConnectGmail,
  activeCampaign,
  activeSegmentKey,
  onClearSegment,
}) {
  const hasNoCampaigns = !loadingCampaigns && (campaigns?.length || 0) === 0;
  const [showNewCampaign, setShowNewCampaign] = useState(() => hasNoCampaigns);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creating, setCreating] = useState(false);

  // If the campaign list finishes loading and comes back empty, proactively
  // reveal the new-campaign form so the onboarding path is a single click.
  const sawEmptyRef = useRef(hasNoCampaigns);
  useEffect(() => {
    if (hasNoCampaigns && !sawEmptyRef.current) {
      sawEmptyRef.current = true;
      setShowNewCampaign(true);
    }
  }, [hasNoCampaigns]);

  const submitNew = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await onNewCampaign({ name: newName, description: newDescription });
      setNewName("");
      setNewDescription("");
      setShowNewCampaign(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <aside className="flex h-full w-[288px] shrink-0 flex-col border-r border-zinc-200 bg-white">
      <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
            Outreach CRM
          </p>
          <h1 className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-900">
            Leads
          </h1>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onTemplates}
            title="Edit templates"
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100"
          >
            Templates
          </button>
        </div>
      </div>

      {!gmailConnected ? (
        <button
          onClick={onConnectGmail}
          className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left text-xs leading-relaxed text-amber-900 hover:bg-amber-100"
        >
          <p className="font-semibold">Gmail is not connected</p>
          <p className="mt-0.5 text-amber-700">Connect in Settings to send queue items and sync replies.</p>
        </button>
      ) : null}

      <div className="mt-4 px-3">
        <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
          Smart views
        </p>
        <nav className="mt-1 space-y-0.5">
          {SMART_VIEWS.map((view) => {
            const isActive = view.key === smartView;
            const count = countForSmartView(view.key, {
              overview,
              queueRows,
              activityRows,
              memberRows,
            });
            return (
              <button
                key={view.key}
                onClick={() => onSmartViewChange(view.key)}
                className={classNames(
                  "flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-zinc-700 hover:bg-zinc-100"
                )}
              >
                <span className="flex items-center gap-2">
                  <ToneDot tone={view.badgeTone} />
                  <span className="font-medium">{view.label}</span>
                </span>
                {count ? (
                  <span
                    className={classNames(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                      isActive
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-zinc-100 text-zinc-600"
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
      </div>

      {activeCampaign?.savedSegments?.length ? (
        <div className="mt-4 px-3">
          <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
            Saved segments
          </p>
          <div className="mt-1 space-y-0.5">
            {(activeCampaign.savedSegments || []).map((segment) => {
              const key = segment._id || segment.name;
              const isActive = activeSegmentKey === key;
              return (
                <div
                  key={key}
                  className={classNames(
                    "group flex items-center justify-between rounded-lg px-2 py-1.5 text-sm",
                    isActive
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-zinc-700 hover:bg-zinc-100"
                  )}
                >
                  <button
                    onClick={() => onSegmentSelect(segment)}
                    className="flex-1 truncate text-left font-medium"
                  >
                    {segment.name}
                  </button>
                  <button
                    onClick={() => onSegmentDelete?.(key)}
                    className="ml-2 hidden text-zinc-400 hover:text-red-500 group-hover:block"
                    title="Remove segment"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            {activeSegmentKey ? (
              <button
                onClick={onClearSegment}
                className="w-full rounded-lg px-2 py-1.5 text-left text-xs text-zinc-500 hover:bg-zinc-50"
              >
                Clear segment
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="mt-4 flex-1 overflow-y-auto px-3">
        <div className="flex items-center justify-between px-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
            Campaigns
          </p>
          <button
            onClick={() => setShowNewCampaign((v) => !v)}
            className="rounded-md px-1.5 py-0.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
          >
            {showNewCampaign ? "Close" : "+ New"}
          </button>
        </div>

        {showNewCampaign ? (
          <div className="mt-2 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Campaign name"
              className="w-full rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Brief (optional)"
              rows={2}
              className="mt-2 w-full resize-none rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
            <button
              onClick={submitNew}
              disabled={creating || !newName.trim()}
              className="mt-2 w-full rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
            >
              {creating ? "Creating..." : "Create campaign"}
            </button>
          </div>
        ) : null}

        <div className="mt-2 space-y-0.5 pb-2">
          {loadingCampaigns ? (
            <p className="px-2 py-2 text-xs text-zinc-400">Loading campaigns...</p>
          ) : campaigns.length ? (
            campaigns.map((campaign) => {
              const isActive = campaign._id === activeCampaignId;
              const stats = campaign.stats || {};
              return (
                <button
                  key={campaign._id}
                  onClick={() => onCampaignChange(campaign._id)}
                  className={classNames(
                    "w-full rounded-lg px-2 py-1.5 text-left transition-colors",
                    isActive
                      ? "bg-zinc-900 text-white"
                      : "hover:bg-zinc-100"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <p
                      className={classNames(
                        "truncate text-sm font-medium",
                        isActive ? "text-white" : "text-zinc-900"
                      )}
                    >
                      {campaign.name}
                    </p>
                    <span
                      className={classNames(
                        "ml-2 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                        isActive
                          ? "bg-white/10 text-zinc-100"
                          : campaign.status === "active"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-zinc-100 text-zinc-600"
                      )}
                    >
                      {campaign.status}
                    </span>
                  </div>
                  <div
                    className={classNames(
                      "mt-0.5 flex items-center gap-2 text-[11px] tabular-nums",
                      isActive ? "text-zinc-300" : "text-zinc-500"
                    )}
                  >
                    <span>{stats.enrolled || 0} enrolled</span>
                    <span>·</span>
                    <span>{stats.pendingQueue || 0} pending</span>
                    <span>·</span>
                    <span>{stats.replied || 0} replies</span>
                  </div>
                </button>
              );
            })
          ) : (
            <p className="px-2 py-2 text-xs text-zinc-400">No campaigns yet.</p>
          )}
        </div>
      </div>

      <div className="border-t border-zinc-100 p-3">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onImport}
            disabled={!activeCampaignId}
            className="rounded-lg border border-zinc-300 bg-white px-2.5 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:text-zinc-400"
          >
            Import CSV
          </button>
          <button
            onClick={onOpenSettings}
            disabled={!activeCampaignId}
            className="rounded-lg border border-zinc-300 bg-white px-2.5 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:text-zinc-400"
          >
            Campaign settings
          </button>
        </div>
      </div>
    </aside>
  );
}
